<template>
  <svg
    class="metric-icon"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    stroke-width="1.7"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <!-- Gameplays / sessions -->
    <template v-if="kind === 'gameplays'">
      <rect x="2.5" y="8" width="19" height="10" rx="3.5" />
      <path d="M7 13 L10 13" />
      <path d="M8.5 11.5 L8.5 14.5" />
      <circle cx="15.5" cy="12.5" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="17.5" cy="14" r="0.75" fill="currentColor" stroke="none" />
    </template>

    <!-- Registrations (user with plus) -->
    <template v-else-if="kind === 'registrations'">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 21 C3 16.5 5.5 14.5 9 14.5 C10.3 14.5 11.4 14.7 12.4 15.1" />
      <path d="M18 14 L18 20" />
      <path d="M15 17 L21 17" />
    </template>

    <!-- Generation tokens (sparkle) -->
    <template v-else-if="kind === 'generation'">
      <path d="M12 3 L13.4 9.2 L19.6 10.6 L13.4 12 L12 18.2 L10.6 12 L4.4 10.6 L10.6 9.2 Z" />
      <path d="M18.5 17 L19 18.7 L20.7 19.2 L19 19.7 L18.5 21.4 L18 19.7 L16.3 19.2 L18 18.7 Z" />
    </template>

    <!-- WhatsApp / messages -->
    <template v-else-if="kind === 'whatsapp'">
      <path d="M3 12 C3 7 7 4 12 4 C17 4 21 7 21 12 C21 17 17 20 12 20 C10.5 20 9 19.7 7.7 19.2 L4 20 L4.8 16.3 C3.7 15.1 3 13.6 3 12 Z" />
    </template>

    <!-- Team members (two people) -->
    <template v-else-if="kind === 'team'">
      <circle cx="9" cy="8" r="3.4" />
      <path d="M3 20 C3 16 5.7 14 9 14 C12.3 14 15 16 15 20" />
      <circle cx="17" cy="9.5" r="2.4" />
      <path d="M17 14 C19.3 14 21 16 21 20" />
    </template>

    <!-- Campaigns (flag) -->
    <template v-else-if="kind === 'campaigns'">
      <path d="M5 21 L5 4" />
      <path d="M5 4 L18 4 L15 8.5 L18 13 L5 13" />
    </template>

    <!-- Tenants / sub-brands (building) -->
    <template v-else-if="kind === 'tenants'">
      <rect x="5" y="3" width="14" height="18" rx="1.5" />
      <path d="M9 7 L11 7" />
      <path d="M13 7 L15 7" />
      <path d="M9 11 L11 11" />
      <path d="M13 11 L15 11" />
      <path d="M11 21 L11 16.5 L13 16.5 L13 21" />
    </template>

    <!-- Scheduler months (calendar) -->
    <template v-else-if="kind === 'calendar'">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10 L21 10" />
      <path d="M8 3 L8 7" />
      <path d="M16 3 L16 7" />
      <circle cx="8" cy="14.5" r="0.85" fill="currentColor" stroke="none" />
      <circle cx="12" cy="14.5" r="0.85" fill="currentColor" stroke="none" />
      <circle cx="16" cy="14.5" r="0.85" fill="currentColor" stroke="none" />
    </template>

    <!-- Fallback (coin / token) -->
    <template v-else>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 7 L12 17" />
      <path d="M9.5 9.2 C9.5 8 10.5 7.2 12 7.2 C13.5 7.2 14.4 8.1 14.4 9.2 C14.4 10.4 13.5 10.9 12 11 C10.5 11.1 9.5 11.7 9.5 12.9 C9.5 14.1 10.4 14.9 12 14.9 C13.5 14.9 14.5 14.1 14.5 12.9" />
    </template>
  </svg>
</template>

<script lang="ts">
import { defineComponent, computed, type PropType } from 'vue'

export type MetricKind =
  | 'gameplays'
  | 'registrations'
  | 'generation'
  | 'whatsapp'
  | 'team'
  | 'campaigns'
  | 'tenants'
  | 'calendar'
  | 'token'

const KEY_TO_KIND: Record<string, MetricKind> = {
  // Token-balance keys
  session_tokens: 'gameplays',
  gameplay_tokens: 'gameplays',
  user_session_tokens: 'team',
  prize_allocation_tokens: 'token',
  registration_tokens: 'registrations',
  voucher_tokens: 'token',
  login_tokens: 'registrations',
  generation_tokens: 'generation',
  whatsapp_credits: 'whatsapp',
  // Account-limit keys
  campaigns: 'campaigns',
  tenants: 'tenants',
  org_users: 'team',
  registrations_included: 'registrations',
  registrations_hard_cap: 'registrations',
  scheduler_months: 'calendar',
  gameplays: 'gameplays',
}

export default defineComponent({
  name: 'MetricIcon',
  props: {
    metricKey: { type: String as PropType<string>, default: '' },
    kindOverride: { type: String as PropType<MetricKind | ''>, default: '' },
  },
  setup(props) {
    const kind = computed<MetricKind>(() => {
      if (props.kindOverride) return props.kindOverride as MetricKind
      return KEY_TO_KIND[props.metricKey] || 'token'
    })
    return { kind }
  },
})
</script>

<style scoped>
.metric-icon {
  display: block;
  flex-shrink: 0;
}
</style>
